import { useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { RequestsRail } from '../../components/portal/RequestsRail';
import { RequestDetail } from '../../components/portal/RequestDetail';
import { NewRequestForm } from '../../components/portal/NewRequestForm';
import { ConfirmationScreen, type ConfirmationTask } from '../../components/portal/ConfirmationScreen';
import { BoardToggle } from '../../components/portal/BoardToggle';
import { usePortalRequests } from '../../hooks/usePortalRequests';
import { useMyExternalProjects } from '../../hooks/useMyExternalProjects';

/**
 * Phase 57 Plan 57-01 — PORTAL-01
 * Phase 57 Plan 57-02 — PORTAL-02 (RequestsRail mounted)
 * Phase 57 Plan 57-03 — PORTAL-03 (RequestDetail mounted)
 * Phase 57 Plan 57-04 — PORTAL-04 (NewRequestForm + ConfirmationScreen mounted)
 * Phase 57 Plan 57-05 — PORTAL-05 (BoardToggle + view state + navigation)
 *
 * Portal requests page shell. Reads URL params and renders the two-column
 * layout container. Rail is live. Detail sub-surfaces mount based on route.
 *
 * Routes handled by this page (all render the same shell with different
 * sub-surface selection based on params + location):
 *   /external/portal/projects/:projectId/requests
 *   /external/portal/projects/:projectId/requests/new
 *   /external/portal/projects/:projectId/requests/:taskId
 *   /external/portal/projects/:projectId/requests/confirm/:taskId
 */
export function PortalRequestsPage() {
  const { projectId, taskId } = useParams<{ projectId: string; taskId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Distinguish /requests/new from /requests and /requests/confirm/:taskId from /:taskId
  // Both /requests and /requests/new yield taskId=undefined, so pathname is the discriminator.
  const isNew = location.pathname.endsWith('/new');
  const isConfirm = location.pathname.includes('/confirm');

  // Plan 57-05: Board⇄Requests view toggle with sessionStorage persistence.
  // Lazy initializer reads the per-project key set by handleViewChange.
  const [view, setView] = useState<'list' | 'board'>(() => {
    return (sessionStorage.getItem('portal:view:' + projectId) as 'list' | 'board') ?? 'list';
  });

  // Selecting "Board" navigates to the existing external kanban route
  // (/external/projects/:id/board) which reads useParams<{ id }> — that's why
  // we NAVIGATE rather than embed <KanbanBoardPage> inline: an inline embed
  // under the portal's :projectId route would receive id=undefined and never load.
  // Selecting "Requests" returns to (or stays on) the portal requests surface.
  function handleViewChange(mode: 'list' | 'board') {
    setView(mode);
    sessionStorage.setItem('portal:view:' + projectId, mode);
    if (mode === 'board') {
      navigate('/external/projects/' + projectId + '/board');
    } else {
      navigate('/external/portal/projects/' + projectId + '/requests');
    }
  }

  // Board cache — used to enrich the ConfirmationScreen with code/tier from the
  // reconciled task after the optimistic update resolves.
  const boardQuery = usePortalRequests(projectId ?? '');
  const projectsQuery = useMyExternalProjects();

  // Resolve project name for confirmation copy
  const projectName =
    projectsQuery.data?.find((p) => String(p.id) === projectId)?.name ?? 'the project';

  // Build ConfirmationTask from the board cache when the confirm route is active
  function buildConfirmTask(): ConfirmationTask | null {
    if (!taskId) return null;
    const numId = Number(taskId);
    if (!boardQuery.data) return null;
    for (const col of boardQuery.data.columns) {
      const found = col.tasks.find((t) => t.id === numId);
      if (found) {
        return {
          id: found.id,
          name: found.name,
          code: found.code ?? null,
          tierOrderIndex: null, // TaskCard does not yet carry tier orderIndex
          attachmentCount: 0,
          pmFirstName: null,
          pmShortName: null,
          assigneeShortName: null,
        };
      }
    }
    return null;
  }

  return (
    <div className="flex h-full">
      {/* ── Left: Requests rail (320px fixed) ── */}
      <RequestsRail
        projectId={projectId!}
        selectedTaskId={taskId && !isConfirm ? Number(taskId) : null}
        onSelect={(id) =>
          navigate(`/external/portal/projects/${projectId}/requests/${id}`)
        }
        onNew={() => navigate(`/external/portal/projects/${projectId}/requests/new`)}
      />

      {/* ── Right: Detail area ── */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Plan 57-05: BoardToggle anchored to top of the content area.
            "Board" navigates to the existing external kanban route (no inline embed).
            "Requests" navigates back to this portal surface. */}
        <div className="flex items-center gap-2 px-4 pt-4 pb-2">
          <BoardToggle view={view} onChange={handleViewChange} />
        </div>

        {isConfirm && taskId ? (
          // Plan 57-04: ConfirmationScreen
          (() => {
            const confirmTask = buildConfirmTask();
            if (confirmTask) {
              return (
                <ConfirmationScreen
                  task={confirmTask}
                  project={projectName}
                  projectId={projectId!}
                  onOpenRequest={() =>
                    navigate(`/external/portal/projects/${projectId}/requests/${taskId}`)
                  }
                  onSubmitAnother={() =>
                    navigate(`/external/portal/projects/${projectId}/requests/new`)
                  }
                  onGoToDashboard={() =>
                    navigate(`/external/portal/projects/${projectId}/requests`)
                  }
                />
              );
            }
            // Fallback: task not yet in cache (e.g. navigation too fast)
            return (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8">
                <p className="text-ms-text-secondary text-sm">
                  Your request was submitted.{' '}
                  <button
                    type="button"
                    className="text-ms-orange hover:underline font-medium"
                    onClick={() =>
                      navigate(`/external/portal/projects/${projectId}/requests/${taskId}`)
                    }
                  >
                    View it here.
                  </button>
                </p>
              </div>
            );
          })()
        ) : taskId ? (
          // Plan 57-03: RequestDetail
          <RequestDetail taskId={Number(taskId)} projectId={projectId!} />
        ) : isNew ? (
          // Plan 57-04: NewRequestForm
          <NewRequestForm
            projectId={projectId!}
            onCancel={() =>
              navigate(`/external/portal/projects/${projectId}/requests`)
            }
            onCreated={(task) =>
              navigate(`/external/portal/projects/${projectId}/requests/confirm/${task.id}`)
            }
          />
        ) : (
          // Empty state — no task selected, not creating new
          <div
            className="flex-1 flex items-center justify-center text-ms-text-muted text-sm"
            data-testid="empty-detail-placeholder"
          >
            {/* Select a request from the rail to view details */}
          </div>
        )}
      </div>
    </div>
  );
}
